// ── Why the resume probe failed, and when it did not fail at all (S2-02) ────
// `GET /v1/quizzes/:quizId/sessions/active` is the first request the attempt
// page makes. For anyone opening a published quiz for the first time the API's
// answer is a 404: there is no attempt to resume. That is not a failure, it is
// the ordinary path, and reading it as one is why a published quiz link showed
// "We couldn't check for an active attempt" over a black screen instead of the
// pre-screen.
//
// The refusals that *are* failures do not all mean the same thing either. An
// exam credential issued for a different exam is not something a retry fixes,
// so it must not be worded like a blip.

import { ApiError } from "@/lib/api";

/**
 * True when the API is saying "you have no attempt in progress", which is the
 * state every learner is in before they start. The caller shows the pre-screen.
 */
export function isNoActiveSessionRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.code === "QUIZ_SESSION_NOT_ACTIVE") return true;
  // The pre-S2-02 API answered the same thing as a bare 404 `NOT_FOUND`. Kept
  // so a web deploy landing ahead of the API roll is not still broken. The
  // other 404 this route can answer is a quiz that no longer exists, and a
  // learner sent to the pre-screen by that is told the truth the moment they
  // press Start.
  return error.status === 404 && (error.code === "NOT_FOUND" || !error.code);
}

export type ActiveSessionProbeFailure =
  /** An exam credential issued for a *different* exam. Retrying cannot help. */
  | { kind: "exam_scope" }
  /** Any other RBAC refusal on the route: the account may not sit this quiz. */
  | { kind: "forbidden" }
  /** Expired auth, a server fault, a network blip. The shared registry words these. */
  | { kind: "unclassified" };

function readReason(details: unknown): string | null {
  if (!details || typeof details !== "object" || Array.isArray(details)) return null;
  const reason = (details as Record<string, unknown>)["reason"];
  return typeof reason === "string" ? reason : null;
}

/**
 * Only classifies what the shared api-error registry cannot: `RBAC_FORBIDDEN`
 * is platform-wide and carries no copy of its own, yet on this route it is the
 * one refusal a learner can act on.
 */
export function readActiveSessionProbeFailure(error: unknown): ActiveSessionProbeFailure {
  if (!(error instanceof ApiError)) return { kind: "unclassified" };
  // Plain `FORBIDDEN` already has copy in the shared registry; `RBAC_FORBIDDEN`
  // does not, and it is the code both refusals on this route arrive under.
  if (error.code !== "RBAC_FORBIDDEN") return { kind: "unclassified" };
  // `assertExamScopeQuiz` is the only source of this reason.
  return readReason(error.details) === "exam_scope"
    ? { kind: "exam_scope" }
    : { kind: "forbidden" };
}
